/**
 * Employee Type API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  EmployeeType,
  EmployeeTypeListResponse,
  EmployeeTypeResponse,
  EmployeeTypeCreateInput,
  EmployeeTypeUpdateInput,
} from '../types/employee-type';

interface ListEmployeeTypesOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class EmployeeTypeService {
  /**
   * List employee types with pagination.
   */
  async listEmployeeTypes(
    workspaceId: string,
    idToken: string,
    options: ListEmployeeTypesOptions = {}
  ): Promise<EmployeeTypeListResponse> {
    const { page = 1, size = 50, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmployeeTypeListResponse>(`/employee-types`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list employee types');
    }

    return response.data;
  }

  /**
   * Get a single employee type by ID.
   */
  async getEmployeeType(
    workspaceId: string,
    employeeTypeId: string,
    idToken: string
  ): Promise<EmployeeType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmployeeTypeResponse>(`/employee-types/${employeeTypeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get employee type');
    }

    return response.data.data;
  }

  /**
   * Create a new employee type.
   */
  async createEmployeeType(
    workspaceId: string,
    idToken: string,
    data: EmployeeTypeCreateInput
  ): Promise<EmployeeType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<EmployeeTypeResponse>(`/employee-types`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create employee type');
    }

    return response.data.data;
  }

  /**
   * Bulk create employee types; duplicates skipped silently.
   */
  async bulkCreateEmployeeTypes(
    workspaceId: string,
    idToken: string,
    names: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/employee-types/bulk`,
      { names },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create employee types');
    }

    return response.data.data;
  }

  /**
   * Update an employee type.
   */
  async updateEmployeeType(
    workspaceId: string,
    employeeTypeId: string,
    idToken: string,
    data: EmployeeTypeUpdateInput
  ): Promise<EmployeeType> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<EmployeeTypeResponse>(
      `/employee-types/${employeeTypeId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update employee type');
    }

    return response.data.data;
  }

  /**
   * Delete an employee type.
   */
  async deleteEmployeeType(
    workspaceId: string,
    employeeTypeId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/employee-types/${employeeTypeId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const employeeTypeService = new EmployeeTypeService();
export default employeeTypeService;
